import { useCallback, useEffect, useState } from 'react';

import { Linkedin01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import FeatherIcon from 'feather-icons-react';

import { IconButton } from 'components/IconButton/IconButton';
import LinkShare from 'components/LinkShare/LinkShare';

import { useLazyGetRefereeReportUrlQuery } from 'api/referees-api';

import { RefereeMetadataDetailsModal } from '../RefereeMetadataDetailsModal/RefereeMetadataDetailsModal';
import VerificationCard from '../VerificationCard/VerificationCard';
import styles from './ReportHeader.module.scss';

export const ReportHeader = ({
  candidate,
  referee,
  referenceCheck,
  empty,
  onDownloadClick = () => {},
}) => {
  const [link, setLink] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [showMetadataDetails, setShowMetadataDetails] = useState(false);
  const [fetchReportUrl] = useLazyGetRefereeReportUrlQuery();

  const [companyMailValidator, setCompanyMailValidator] = useState();
  const [linkedinValidator, setLinkedinValidator] = useState();
  const [ipValidator, setIpValidator] = useState();

  const handleShareReport = useCallback(async () => {
    setLinkOpen(true);
    setLink('');
    const { data } = await fetchReportUrl(referee.id);
    if (data?.url) setLink(data.url);
  }, [fetchReportUrl, referee?.id]);

  useEffect(() => {
    if (!referee) return;

    if (referee.email === candidate.email) {
      setCompanyMailValidator(
        <div className="verification error">
          <FeatherIcon icon="alert-circle" /> Email addresses match
        </div>,
      );
    } else if (referee.has_company_email) {
      setCompanyMailValidator(
        <div className="verification success">
          <FeatherIcon icon="check-circle" /> Registered Company Email
        </div>,
      );
    } else {
      setCompanyMailValidator(
        <div className="verification warn">
          <FeatherIcon icon="alert-circle" /> Personal email used
        </div>,
      );
    }

    if (referee.linkedin_profile) {
      setLinkedinValidator(
        <div className="verification success">
          <FeatherIcon icon="check-circle" /> Verified Linkedin
        </div>,
      );
    } else {
      setLinkedinValidator(
        <div className="verification warn">
          <FeatherIcon icon="alert-circle" /> No information provided
        </div>,
      );
    }

    if (referee.ip !== candidate.ip) {
      setIpValidator(
        <div className="verification success">
          <FeatherIcon icon="check-circle" /> Unique IP Address
        </div>,
      );
    } else {
      setIpValidator(
        <div className="verification error">
          <FeatherIcon icon="alert-circle" /> IP address matches candidate
        </div>,
      );
    }
  }, [referee]);

  return (
    <div className={styles.root}>
      <RefereeMetadataDetailsModal
        candidate={candidate}
        referee={referee}
        referenceCheck={referenceCheck}
        visible={showMetadataDetails}
        onClose={() => setShowMetadataDetails(false)}
      />
      <div className={styles.titles}>
        <div className={styles.name}>
          <div className={styles.label}>Referee</div>
          <div className={styles.value}>{referee.name}</div>
          <div className={styles.details}>
            {referee.role} @ {referee.company_name}
          </div>
          {referee.answered_on_behalf_user && (
            <div className={styles.details}>
              Answered on behalf of by: {referee.answered_on_behalf_user}
            </div>
          )}
        </div>
        <div>
          <div className="d-flex align-items-center">
            {referee.user_agent && (
              <IconButton
                icon="monitor"
                tip="Metadata details"
                onClick={() => setShowMetadataDetails(true)}
                className="u-margin-right--small"
              />
            )}
            <LinkShare
              open={linkOpen}
              link={link}
              title="Share this report"
              onClose={() => setLinkOpen(false)}
            />
            <IconButton
              icon="share-2"
              tip="Link"
              onClick={handleShareReport}
              className="u-margin-right--small"
            />
            <IconButton icon="download" tip="Download" onClick={onDownloadClick} />
          </div>
        </div>
      </div>
      <div className={styles.verificationCards}>
        <VerificationCard>
          <div className="title">
            <FeatherIcon icon="mail" /> email address
          </div>
          <a href={'mailto:' + referee.email}>{referee.email}</a>
          {companyMailValidator}
        </VerificationCard>
        <VerificationCard>
          <div className="title">
            <HugeiconsIcon
              size="16"
              icon={Linkedin01Icon}
              strokeWidth="1.5"
              className="u-margin-bottom--tiny"
            />{' '}
            linkedin profile
          </div>
          <a href={'mailto:' + referee.linkedin_profile}>
            {referee.linkedin_profile ? referee.linkedin_profile : '-'}
          </a>
          {linkedinValidator}
        </VerificationCard>
        <VerificationCard>
          <div className="title">
            <FeatherIcon icon="shield" /> ip address
          </div>
          <div className="static-value">{referee.ip}</div>
          {ipValidator}
        </VerificationCard>
      </div>
    </div>
  );
};
